import type { ReactNode } from 'react'
import { useMessages } from '@/features/language/use-language'
import { GoogleMark, NaverMark } from './ProviderMark'
import type { ProviderId } from './providers'

interface ProviderButtonProps {
  provider: ProviderId
  /** provider로 떠나는 중이면 라벨을 진행 문구로 바꾸고 버튼을 잠근다. */
  pending?: boolean
  /** 다른 버튼이 왕복을 시작해 이 버튼도 함께 잠긴 상태. 라벨은 그대로 둔다. */
  locked?: boolean
  /**
   * 로그인이 아닌 자리의 라벨.
   *
   * 이 버튼이 로그인 화면 밖에도 서고 거기서 하는 일이 로그인이 아니다. 계속하기로 적으면
   * 이미 있는 공간에 방법을 더하는 일이 로그인과 같은 일로 읽힌다. 떠나는 중의 문구는 어느
   * 자리에서나 같으므로 갈리지 않는다.
   */
  label?: string
  onClick?: () => void
}

/**
 * provider 동의 화면으로 보내는 버튼.
 *
 * 브랜드 가이드가 색과 로고, 라벨 표기를 정해 두어 테마 토큰을 따르지 못한다. 예외는
 * provider별 색 토큰 세 개에 갇혀 있고 밝기에 맞는 변형은 테마 블록이 고르므로 이 컴포넌트에
 * 테마 분기가 없다. provider가 늘면 아래 표에 항목이 하나 붙는 것으로 끝난다.
 *
 * 로고는 왼쪽 16px 자리에 고정하고 라벨은 버튼 가운데에 둔다. 두 로고가 같은 세로선에 서서
 * 버튼 둘이 한 목록으로 읽힌다.
 *
 * 로고는 장식이라 대체 텍스트를 비우고 접근성 이름은 보이는 라벨과 같게 둔다. 떠나는 중에는
 * 두 버튼을 함께 잠그되 진행 중이라는 것은 누른 버튼의 상태 속성으로만 알린다. 잠근 버튼을
 * 포커스 순서에서 빼지 않아 방금 누른 자리로 탭이 돌아올 수 있다.
 *
 * 누른 자리를 알리는 면과 포커스 링은 다른 버튼과 같은 규칙을 쓴다. 브랜드가 정한 것은
 * 면색과 로고, 라벨 표기이고 손에 답하는 방식까지 갈라 둘 까닭이 없다.
 */
export function ProviderButton({ provider, pending, locked, label, onClick }: ProviderButtonProps) {
  const m = useMessages()
  const { logo, tone, emphasis } = PROVIDERS[provider]
  const name = m.auth.provider[provider]
  const shown = pending
    ? m.auth.provider.leavingTo(name)
    : (label ?? m.auth.provider.continueWith(name))

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending || locked}
      aria-busy={pending}
      className={`pressable rounded-card flex h-12 w-full items-center justify-center border px-[18px] text-base disabled:opacity-60 ${tone} ${emphasis}`}
    >
      <span aria-hidden="true" className="absolute left-4 flex items-center">
        {logo}
      </span>
      <span>{shown}</span>
      {pending && (
        <span
          aria-hidden="true"
          className="absolute right-4 size-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70 motion-reduce:animate-none"
        />
      )}
    </button>
  )
}

/**
 * 네이버 라벨만 획을 한 단계 굵게 잡는다. 흰 글자와 초록 면의 대비가 기준에 미치지 못하는데
 * 브랜드가 고정한 값이라 색으로도 크기로도 풀지 못한다. 획 굵기가 남은 하나다.
 */
const PROVIDERS: Record<ProviderId, { logo: ReactNode; tone: string; emphasis: string }> = {
  google: {
    logo: <GoogleMark />,
    tone: 'bg-provider-google-surface text-provider-google-ink border-provider-google-border',
    emphasis: 'font-medium',
  },
  naver: {
    logo: <NaverMark />,
    tone: 'bg-provider-naver-surface text-provider-naver-ink border-provider-naver-border',
    emphasis: 'font-semibold',
  },
}
